import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { API } from "../constant/api";
import useAuthAxios from "../utils/baseAxios";

const SELECTED_BUSINESS_KEY = "merchant-selected-business-id";

export type BusinessListItem = {
  _id: string;
  name?: string;
  email?: string;
  /** Public-bucket URL — safe to render directly, no signing needed. */
  logo?: string;
  type?: "STARTER" | "REGISTERED" | string;
  status?: string;
  isVerified?: boolean;
  details?: {
    industry?: string;
    address?: string;
    size?: string;
    country?: string;
    description?: string;
  };
  parentBusinessId?: string;
  role?: "owner" | "manager" | string;
  banks?: unknown[];
  [key: string]: unknown;
};

export type BusinessesMeta = {
  count: number;
  max: number;
  canCreateMore: boolean;
  blockedReason: string | null;
};

type BusinessesPayload = {
  businesses: BusinessListItem[];
  meta: BusinessesMeta;
};

const DEFAULT_META: BusinessesMeta = {
  count: 0,
  max: 5,
  canCreateMore: true,
  blockedReason: null,
};

const readStoredBusinessId = (): string | null => {
  try {
    return localStorage.getItem(SELECTED_BUSINESS_KEY);
  } catch {
    return null;
  }
};

const writeStoredBusinessId = (id: string) => {
  try {
    localStorage.setItem(SELECTED_BUSINESS_KEY, id);
  } catch {
    // Ignore quota / private-mode failures — selection still works in-memory.
  }
};

/**
 * Shared selected-business store.
 *
 * `useBusinesses` is called from many components (navbar, dashboard, payment…).
 * A per-hook `useState` meant only the clicker saw the switch — everyone else
 * kept the old id until a full remount/refresh re-read localStorage.
 */
let selectedBusinessId: string | null = readStoredBusinessId();
const selectedBusinessListeners = new Set<() => void>();

const subscribeSelectedBusiness = (onStoreChange: () => void) => {
  selectedBusinessListeners.add(onStoreChange);
  return () => {
    selectedBusinessListeners.delete(onStoreChange);
  };
};

const getSelectedBusinessSnapshot = () => selectedBusinessId;

const setSelectedBusinessId = (id: string) => {
  if (selectedBusinessId === id) return;
  selectedBusinessId = id;
  writeStoredBusinessId(id);
  selectedBusinessListeners.forEach((listener) => listener());
};

/** Wipe the in-memory + localStorage selection on logout so a different
 *  account doesn't inherit the previous user's active business. */
export const clearSelectedBusiness = () => {
  selectedBusinessId = null;
  try {
    localStorage.removeItem("merchant-selected-business-id");
  } catch {
    // Ignore
  }
  selectedBusinessListeners.forEach((listener) => listener());
};

/** Human-readable copy for `meta.blockedReason` from GET /api/businesses. */
export const createBusinessBlockedMessage = (
  reason: string | null | undefined
): string => {
  switch (reason) {
    case "PENDING_REVIEW":
      return "You have a business pending review. Wait for approval before creating another.";
    case "MAX_REACHED":
      return "You've reached the maximum of 5 businesses.";
    default:
      return "You can't create another business right now.";
  }
};

/**
 * The merchant's business list + which one is currently active.
 *
 * Selection is client-side (shared store + localStorage): the API returns every
 * business and a `meta` gate for creating more, but does not track an "active"
 * business. Every authenticated screen keys off `businessId` from this hook.
 */
export const useBusinesses = () => {
  const axiosInstance = useAuthAxios();
  const selectedId = useSyncExternalStore(
    subscribeSelectedBusiness,
    getSelectedBusinessSnapshot,
    getSelectedBusinessSnapshot
  );

  const query = useQuery({
    queryKey: ["get-businesses"],
    queryFn: async (): Promise<BusinessesPayload> => {
      const response = await axiosInstance.get(API.getBusinesses);
      const data = response?.data?.data;
      return {
        businesses: data?.businesses ?? [],
        meta: {
          count: data?.meta?.count ?? data?.businesses?.length ?? 0,
          max: data?.meta?.max ?? 5,
          canCreateMore: data?.meta?.canCreateMore ?? true,
          blockedReason: data?.meta?.blockedReason ?? null,
        },
      };
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const businesses = query.data?.businesses;
  const meta = query.data?.meta ?? DEFAULT_META;

  // Keep selection valid when the list loads or shrinks (deleted / filtered).
  useEffect(() => {
    if (!businesses?.length) return;

    const stillValid = selectedId
      ? businesses.some((b) => b._id === selectedId)
      : false;

    if (!stillValid) {
      setSelectedBusinessId(businesses[0]._id);
    }
  }, [businesses, selectedId]);

  const selectBusiness = useCallback((id: string) => {
    setSelectedBusinessId(id);
  }, []);

  const business = useMemo(
    () => businesses?.find((b) => b._id === selectedId),
    [businesses, selectedId]
  );

  // Prefer the validated selection; fall back to [0] only while the effect
  // above hasn't synced yet (first paint after fetch).
  const businessId =
    (business?._id as string | undefined) ??
    (businesses?.[0]?._id as string | undefined);

  return {
    ...query,
    businesses,
    meta,
    business,
    businessId,
    selectBusiness,
    hasBusiness: !!businesses && businesses.length > 0,
    canCreateMore: meta.canCreateMore,
    blockedReason: meta.blockedReason,
  };
};
